"use client";

import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

import { Container } from "@/app/(marketing)/_components/ui/container";

import ExpertsHeroBadge from "../../_components/experts-hero-badge";
import type { AudienceSlug } from "../_config/audiences";
import { TrackedCtaLink } from "./tracked-cta-link";

interface AudienceHeroProps {
  audience: AudienceSlug;
  eyebrow: string;
  title: string;
  subtitle: string;
  ctaHref: string;
  ctaLabel?: string;
  initialPersonaId?: string;
}

export default function AudienceHero({
  audience,
  eyebrow,
  title,
  subtitle,
  ctaHref,
  ctaLabel = "Claim your profile",
  initialPersonaId,
}: AudienceHeroProps) {
  return (
    <section className="relative px-5 pt-16 pb-28 md:px-8 md:pt-20 md:pb-32 lg:px-14">
      <Container>
        <motion.div
          initial={{ opacity: 0, transform: "translateY(20px)" }}
          animate={{ opacity: 1, transform: "translateY(0px)" }}
          transition={{ duration: 0.5 }}
          className="mx-auto flex w-full max-w-[914px] flex-col items-center"
        >
          <ExpertsHeroBadge
            href={ctaHref}
            initialPersonaId={initialPersonaId}
            wrapperClassName="pointer-events-none relative z-10 -mt-[460px] select-none"
            audience={audience}
            ctaLocation="hero_badge"
          />

          <span className="mt-8 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 font-mono text-[11px] tracking-[0.18em] text-white/90 uppercase backdrop-blur-sm">
            {eyebrow}
          </span>

          <div className="mt-5 flex flex-col items-center gap-5 text-center text-white">
            <h1 className="max-w-[820px] text-4xl leading-[1.05] font-medium tracking-tight text-balance sm:text-5xl md:text-[56px] md:leading-[1.05]">
              {title}
            </h1>
            <p className="max-w-[680px] text-base leading-relaxed text-balance text-white/80 md:text-lg">
              {subtitle}
            </p>
          </div>

          <TrackedCtaLink
            href={ctaHref}
            audience={audience}
            ctaLocation="hero_button"
            className="text-marketing-blue group mt-8 inline-flex h-12 items-center justify-center gap-2 rounded-full bg-white px-8 text-base font-medium transition-colors hover:bg-white/90"
          >
            {ctaLabel}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </TrackedCtaLink>
        </motion.div>
      </Container>
    </section>
  );
}
